import { useState, useEffect } from "react";
import Icon from "../icons/Icon";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import { ROLE_LIMITS } from "../../config/roles";
import MatrixPicker from "./MatrixPicker";
import "../../styles/components/settings-pages.css";
import "../../styles/components/roles-page.css";

const functions = getFunctions(undefined, "asia-southeast1");
const createCustomRoleFn = httpsCallable(functions, "createCustomRole");
const updateCustomRoleFn = httpsCallable(functions, "updateCustomRole");

const NAME_MAX = ROLE_LIMITS.NAME_MAX;
const DESCRIPTION_MAX = ROLE_LIMITS.DESCRIPTION_MAX;

const INITIAL_FORM = {
  name: "",
  description: "",
  permissions: [],
};

const RoleEditorPage = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { roleId } = useParams();
  const mode = roleId ? "edit" : "create";

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);
  const [permissionsError, setPermissionsError] = useState(null);

  const basePath = `/company/${company?.slug || ""}/settings`;
  const rolesPath = `${basePath}/roles`;

  // Load existing role in edit mode.
  useEffect(() => {
    if (mode !== "edit" || !company?.id || !roleId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(
            db,
            "companies",
            company.id,
            "settings",
            "roles",
            "custom",
            roleId,
          ),
        );
        if (cancelled) return;
        if (!snap.exists()) {
          toast.error("Role not found.");
          navigate(rolesPath);
          return;
        }
        const data = snap.data();
        setForm({
          name: data.name || "",
          description: data.description || "",
          permissions: Array.isArray(data.permissions) ? data.permissions : [],
        });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load role:", err);
        toast.error("Failed to load role.");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, company?.id, roleId, navigate, rolesPath, toast]);

  const setField = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (field === "name") setNameError(null);
    if (field === "description") setDescriptionError(null);
    if (field === "permissions") setPermissionsError(null);
  };

  const validate = () => {
    let ok = true;
    const trimmedName = form.name.trim();
    if (trimmedName.length < 1) {
      setNameError("Role name is required.");
      ok = false;
    } else if (trimmedName.length > NAME_MAX) {
      setNameError(`Role name cannot exceed ${NAME_MAX} characters.`);
      ok = false;
    }
    if (form.description.length > DESCRIPTION_MAX) {
      setDescriptionError(
        `Description cannot exceed ${DESCRIPTION_MAX} characters.`,
      );
      ok = false;
    }
    if (!Array.isArray(form.permissions) || form.permissions.length === 0) {
      setPermissionsError("Select at least one permission.");
      ok = false;
    }
    return ok;
  };

  const mapServerError = (err) => {
    const code = err?.details?.code;
    switch (code) {
      case "NAME_CONFLICT":
        setNameError("A role with this name already exists.");
        return "A role with this name already exists.";
      case "LIMIT_REACHED":
        return "This company already has the maximum of 5 custom roles.";
      case "NON_DELEGATABLE_PERMISSION":
        return "One or more selected permissions are reserved for Superusers and cannot be assigned via custom roles.";
      case "ESCALATION_DENIED":
        return "You cannot grant permissions you don't hold.";
      case "MIN_PERMISSIONS":
        setPermissionsError("Select at least one permission.");
        return "Select at least one permission.";
      default:
        return err?.message || "Something went wrong. Please try again.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!validate()) return;

    setSaving(true);
    try {
      if (mode === "create") {
        await createCustomRoleFn({
          name: form.name.trim(),
          description: form.description.trim(),
          permissions: form.permissions,
        });
        toast.success(`Role "${form.name.trim()}" created.`);
      } else {
        const result = await updateCustomRoleFn({
          roleId,
          name: form.name.trim(),
          description: form.description.trim(),
          permissions: form.permissions,
        });
        const { failedUserIds = [], updatedUserCount = 0 } = result?.data || {};
        if (failedUserIds.length > 0) {
          toast.warning(
            `Role saved, but ${failedUserIds.length} user(s) could not be refreshed. They'll pick up the new permissions on next login.`,
          );
        } else if (updatedUserCount > 0) {
          toast.success(
            `Role saved. ${updatedUserCount} user(s) updated with new permissions.`,
          );
        } else {
          toast.success("Role saved.");
        }
      }
      navigate(rolesPath);
    } catch (err) {
      console.error("Save role failed:", err);
      const message = mapServerError(err);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(rolesPath);
  };

  if (loading) {
    return (
      <div className="role-editor-loading">
        <div className="spinner" />
        <p>Loading role…</p>
      </div>
    );
  }

  return (
    <div className="role-editor">
      <div className="role-editor-breadcrumb">
        <button
          type="button"
          className="role-editor-back"
          onClick={handleCancel}
        >
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Back to Roles
        </button>
      </div>

      <div className="role-editor-header">
        <div>
          <h1 className="settings-page-title">
            {mode === "create" ? "Create Custom Role" : "Edit Custom Role"}
          </h1>
          <p className="settings-page-subtitle">
            Pick the access level for each area. Cross-area dependencies surface
            automatically. Changes propagate to assigned users within one hour
            or on next login.
          </p>
        </div>
        <div className="role-editor-actions">
          <button
            type="button"
            className="settings-btn settings-btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="role-editor-form"
            className="settings-btn settings-btn-primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Role"}
          </button>
        </div>
      </div>

      <form id="role-editor-form" onSubmit={handleSubmit} noValidate>
        {permissionsError && (
          <div
            className="form-error role-editor-permissions-error"
            role="alert"
          >
            {permissionsError}
          </div>
        )}
        <div className="role-editor-card">
          <div className="role-editor-form-row">
            <div className="form-field">
              <label htmlFor="role-name">Role Name *</label>
              <input
                id="role-name"
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                maxLength={NAME_MAX}
                placeholder="e.g. Payroll Specialist"
                disabled={saving}
                aria-invalid={!!nameError}
              />
              {nameError ? (
                <div className="form-error">{nameError}</div>
              ) : (
                <div className="form-hint">
                  Unique per company · max {NAME_MAX} characters
                </div>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="role-description">Description</label>
              <input
                id="role-description"
                type="text"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                maxLength={DESCRIPTION_MAX}
                placeholder="Optional — shown when picking this role"
                disabled={saving}
                aria-invalid={!!descriptionError}
              />
              {descriptionError ? (
                <div className="form-error">{descriptionError}</div>
              ) : (
                <div className="form-hint">
                  Optional · max {DESCRIPTION_MAX} characters
                </div>
              )}
            </div>
          </div>
        </div>

        <MatrixPicker
          value={form.permissions}
          onChange={(next) => setField("permissions", next)}
          disabled={saving}
        />
      </form>
    </div>
  );
};

export default RoleEditorPage;
